// UI adapter — maps CatalogProduct (from Redux/ecomCatalog) to the render props
// the CategoryListingPage product grid cards expect. Keeps the grid dumb and
// decoupled from the raw API shape, which has nested featuredOffer/reviews.
import type { CatalogProduct } from "../../../Redux/ecomCatalog/types";

export interface CategoryCardData {
  id: string;              // MongoDB ObjectId string
  ssin: string;
  name: string;
  description: string;       // product description from catalog
  image: string;
  brandName: string;
  brandLogo?: string;
  price: number;          // featuredOffer.finalPrice
  mrp: number;            // featuredOffer.unitPrice (or mrp fallback)
  discountPercent: number;
  savingsAmount: number;
  rating: number;          // reviews.averageRating
  reviewCount: number;     // reviews.totalReviews
  colors: string[];        // enriched by backend (ProductVariant.variationAttributes.Color)
  freeDelivery: boolean;
  fastDelivery: boolean;
  nextDayDelivery: boolean;
  stockStatus: string;     // in_stock | low_stock | out_of_stock
  isSponsored: boolean;
  dealLabel?: string;      // from featuredOffer.deal
  couponLabel?: string;    // from featuredOffer.coupon
  bogoOffers: { title: string; benefitQuantity: number }[];
}

/**
 * Map a CatalogProduct (raw API shape) to the flat CategoryCardData the grid uses.
 * Defensive against missing featuredOffer / reviews / brand, which can happen on
 * out-of-stock or partially-approved catalog products.
 */
export function mapCatalogProductToCard(p: CatalogProduct): CategoryCardData {
  const fo = (p.featuredOffer ?? {}) as any;
  const mrp = Number(fo.unitPrice ?? fo.mrp ?? fo.finalPrice ?? 0);
  const finalPrice = Number(fo.finalPrice ?? 0);
  const discountPercent =
    Number(
      fo.discountPercent ||
      fo.savingsPercent ||
      fo.deal?.discountPercent ||
      (mrp > finalPrice ? Math.round(((mrp - finalPrice) / mrp) * 100) : 0)
    );

  return {
    id: String(p._id),
    ssin: p.ssin,
    name: p.name,
    description: p.description ?? "",
    image: p.mainImage,
    brandName: p.brand?.name ?? "Unknown",
    brandLogo: p.brand?.logo,
    price: finalPrice,
    mrp,
    discountPercent,
    savingsAmount: Number(fo.savingsAmount ?? (mrp > finalPrice ? mrp - finalPrice : 0)),
    rating: p.reviews?.averageRating ?? 0,
    reviewCount: p.reviews?.totalReviews ?? 0,
    colors: (p as any).colors ?? [],
    freeDelivery: Boolean(fo.freeDelivery),
    fastDelivery: Boolean(fo.fastDelivery),
    nextDayDelivery: Boolean(fo.nextDayDelivery),
    stockStatus: p.stockStatus ?? "in_stock",
    isSponsored: Boolean((p as any).isSponsored),
    dealLabel: fo.deal ? (fo.deal.dealTitle || `${fo.deal.discountPercent}% off`) : undefined,
    couponLabel: fo.coupon?.title,
    bogoOffers: (p.bogoOffers ?? []).map((b) => ({
      title: b.title,
      benefitQuantity: b.benefitQuantity,
    })),
  };
}

/** Format a price for Indian locale (e.g. 1549 → "1,549"). */
export const formatPrice = (value: number) =>
  new Intl.NumberFormat("en-IN").format(value);

/** Slugify a category name for URL segments (Men Fashion → men-fashion). */
export const slugify = (name: string) =>
  name.toLowerCase().replace(/\s+/g, "-");

export type MobileCategoryFilterSection =
  | "sort"
  | "price"
  | "delivery"
  | "brands"
  | "ratings"
  | "deals"
  | "colors"
  | "materials"
  | "discounts";

export const mobileCategoryFilterSections: {
  id: MobileCategoryFilterSection;
  label: string;
}[] = [
  { id: "sort", label: "Sort" },
  { id: "price", label: "Price" },
  { id: "delivery", label: "Delivery Day" },
  { id: "brands", label: "Brands" },
  { id: "ratings", label: "Customer Reviews" },
  { id: "deals", label: "Deals & Discounts" },
  { id: "colors", label: "Colors" },
  { id: "materials", label: "Material Type" },
  { id: "discounts", label: "Discount Range" },
];
